// MarketStructureLenses — two compact distributions (Use Case, Mechanism)
// segmented by curated role, answering: which functional areas of
// tokenization are structurally active right now?
//
// Pure visual; reads from already-normalised market tracks.

import React, { useMemo } from "react";
import { groupTracksBy, SEGMENT_ORDER, SEGMENT_LABELS } from "../../lib/tracks";

const SEGMENT_BAR_TONE = {
    core: "bg-[hsl(var(--ink))]",
    watchlist: "bg-[hsl(var(--warning))]",
    subPattern: "bg-[hsl(var(--mute))]",
    earlyStage: "bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))]",
};

const Lens = ({ title, eyebrow, rows, testId, emptyHint }) => {
    const max = Math.max(1, ...rows.map((r) => r.total));
    return (
        <div className="card-flat p-5 flex flex-col" data-testid={testId}>
            <div className="eyebrow">{eyebrow}</div>
            <h3 className="display text-lg mt-1 leading-snug">{title}</h3>
            {rows.length === 0 ? (
                <p className="text-xs text-[hsl(var(--mute))] mt-3 leading-relaxed">{emptyHint}</p>
            ) : (
                <ul className="mt-4 space-y-2.5">
                    {rows.map((r) => (
                        <li key={r.key} className="flex items-center gap-3" data-testid={`${testId}-row-${r.key}`}>
                            <span className="text-xs text-[hsl(var(--ink-soft))] w-44 truncate flex-none" title={r.key}>{r.key}</span>
                            <div className="flex-1 flex h-2 rounded-sm overflow-hidden bg-[hsl(var(--surface-sunken))]">
                                {SEGMENT_ORDER.map((seg) => {
                                    const v = r.segments[seg] || 0;
                                    if (v === 0) return null;
                                    const pct = (v / max) * 100;
                                    return (
                                        <div
                                            key={seg}
                                            className={`h-full ${SEGMENT_BAR_TONE[seg]}`}
                                            style={{ width: `${pct}%` }}
                                            title={`${v} ${SEGMENT_LABELS[seg].toLowerCase()}${v === 1 ? "" : "s"}`}
                                        />
                                    );
                                })}
                            </div>
                            <span className="text-sm font-semibold num text-[hsl(var(--ink))] w-8 text-right">{r.total}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const Legend = () => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[hsl(var(--mute))]" data-testid="lenses-legend">
        {SEGMENT_ORDER.map((seg) => (
            <span key={seg} className="inline-flex items-center gap-1.5">
                <span className={`inline-block w-3 h-2 rounded-sm ${SEGMENT_BAR_TONE[seg]}`} />
                {SEGMENT_LABELS[seg]}
            </span>
        ))}
    </div>
);

const MarketStructureLenses = ({ tracks }) => {
    const useCaseRows = useMemo(() => groupTracksBy(tracks || [], "PrimaryUseCase"), [tracks]);
    const mechanismRows = useMemo(() => groupTracksBy(tracks || [], "PrimaryMechanism"), [tracks]);

    if (!useCaseRows.length && !mechanismRows.length) return null;

    return (
        <section className="space-y-3" data-testid="market-structure-lenses">
            <header>
                <div className="eyebrow">Market structure lenses</div>
                <h2 className="display text-xl mt-1">Where durable signals are forming by use case and mechanism</h2>
            </header>
            <Legend />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Lens
                    title="Signals by use case"
                    eyebrow="Functional area"
                    rows={useCaseRows}
                    testId="lens-use-case"
                    emptyHint="No use-case data on the current market signals."
                />
                <Lens
                    title="Signals by mechanism"
                    eyebrow="Market mechanism"
                    rows={mechanismRows}
                    testId="lens-mechanism"
                    emptyHint="No mechanism data on the current market signals."
                />
            </div>
        </section>
    );
};

export default MarketStructureLenses;
